import isServer from '../is-server';
import logger from '../logger';
import checkToken from './check-token';
import makeAPIRequestFunc from './make-api-request';

export const makeAPIRequest = makeAPIRequestFunc;

const registerNewLogin = (req) => {
  makeAPIRequest('v2/user-devices', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${req.session.jwt}`,
    },
    body: JSON.stringify({
      app: 'pitchero',
      app_version: `${req.hostname} v6 ${req.headers['user-agent']}`,
      device_id: `${req.hostname}-${req.sessionID}`,
      device_type: 'Browser',
      session_id: req.sessionID,
    }),
  });
};

const proxyAPIRequest = (endpoint, options, req, res) =>
  new Promise((resolve) => {
    checkToken(req).then((token) => {
      const requestOptions = options || {};
      requestOptions.headers = requestOptions.headers || {};
      // Passing the req.ip enables us to throttle requests on the LAMP
      // stack based on the user IP rather than the NodeJS IP.
      requestOptions.headers['X-PH-Client-Ip'] = req.ip;
      if (token) {
        requestOptions.headers.Authorization = `Bearer ${token}`;
      }

      makeAPIRequest(endpoint, requestOptions)
        .then((apiResponse) => {
          const { status } = apiResponse;
          logger.info('API response received', { status });

          if (!res.headersSent) {
            res.status(status);
          }

          return apiResponse.json();
        })
        .then((data) => {
          res.json(data);
          res.end();
          resolve();
        })
        .catch((err) => {
          logger.info('API request error', { err });
          const { statusCode, headersSent } = res;

          // Default to 500 if no headers, although if we have a status code from the response,
          // we should always aim to use this. use the direct HTTP response codes from the API
          if (!headersSent || headersSent === undefined) {
            res.status(500);
          }

          if (statusCode) {
            res.status(statusCode);
          }

          res.end();
          resolve();
        });
    });
  });

/**
 * Server side this will directly fetch from the API, Client side it will fetch via the node proxy.
 * @param endpoint
 * @param options
 * @returns {Promise<*>}
 */
export const makeIsomorphicApiRequest = async (endpoint, options) => {
  if (isServer()) {
    return makeAPIRequest(endpoint, options);
  }

  const proxyUrl = `/api/${endpoint.replace(/^\/+/, '')}`;
  logger.info('Proxying API request', { url: proxyUrl });
  const fetchOptions = options || {};
  fetchOptions.headers = fetchOptions.headers || {};
  fetchOptions.headers['Content-Type'] = 'application/json';
  return fetch(proxyUrl, { credentials: 'include', ...fetchOptions });
};

const proxyLoginRequest = (req, res) =>
  makeAPIRequest('v2/auth/jwt', {
    method: 'POST',
    body: JSON.stringify(req.body),
  })
    .then((response) => {
      logger.info('login response received', response.status);
      if (response.status !== 201) {
        res.writeHead(response.status);
        res.end();
        throw new Error('Login failed');
      }

      return response.json();
    })
    .then((data) => {
      req.session.jwt = data.token;
      if (!res.headersSent) {
        res.writeHead(200);
      }
      res.write(JSON.stringify({ message: 'Login successful' }));
      res.end();
      registerNewLogin(req);
    })
    .catch((err) => {
      logger.info('API request error', { err });
      if (!res.headersSent) {
        res.writeHead(res.statusCode);
      }
      res.write(JSON.stringify({ message: 'Login unsuccessful' }));
      res.end();
    });

const proxyTokenLoginRequest = (req, res) =>
  makeAPIRequest('v2/auth/token-login', {
    method: 'POST',
    body: JSON.stringify(req.body),
  })
    .then((response) => {
      logger.info('token login response received', response.status);

      const validLoginStatus = response.status === 200 || response.status === 201;

      if (!validLoginStatus) {
        throw new Error('Login failed');
      }

      return response.json();
    })
    .then((data) => {
      req.session.jwt = data.token;
      if (!res.headersSent) {
        res.writeHead(201);
      }

      res.write(JSON.stringify({ message: 'Login successful' }));
      res.end();
      registerNewLogin(req);
    })
    .catch((err) => {
      logger.info('API request error', { err });

      if (!res.headersSent) {
        res.writeHead(401);
      }

      res.write(JSON.stringify({ message: 'Login unsuccessful' }));
      res.end();
    });

export const proxyGetRequest = async (req, res) => {
  const endpoint = req.originalUrl.substr(5);
  const requestOptions = {
    method: 'GET',
  };

  return proxyAPIRequest(endpoint, requestOptions, req, res);
};

export const proxyDeleteRequest = async (req, res) => {
  const endpoint = req.originalUrl.substr(5);
  const requestOptions = {
    method: 'DELETE',
  };

  return proxyAPIRequest(endpoint, requestOptions, req, res);
};

export const proxyPostRequest = (req, res) => {
  const endpoint = req.originalUrl.substr(5);
  if (endpoint === 'v2/auth/jwt') {
    return proxyLoginRequest(req, res);
  }

  if (endpoint === 'v2/auth/token-login') {
    return proxyTokenLoginRequest(req, res);
  }

  const requestOptions = {
    method: 'POST',
    body: JSON.stringify(req.body),
  };

  return proxyAPIRequest(endpoint, requestOptions, req, res);
};

export const proxyPutRequest = async (req, res) => {
  const endpoint = req.originalUrl.substr(5);
  const requestOptions = {
    method: 'PUT',
    body: JSON.stringify(req.body),
  };

  return proxyAPIRequest(endpoint, requestOptions, req, res);
};
